/**
 * Logger client-side observable.
 *
 * Ring buffer en sessionStorage (jf-debug-log) con cap de 100 entries.
 * Fallback a memoria si sessionStorage no esta disponible (SSR / modo privado).
 *
 * Reglas de seguridad:
 *   - NUNCA guarda: request bodies, response bodies, tokens, cookies, OTP,
 *     passwords. La capa sanitizeMeta() impone una whitelist estricta de claves.
 *   - tenant_id se lee del store de auth (Usuario.tenant), nunca decodificando
 *     el JWT manualmente.
 *
 * Uso:
 *   logger.log({ category: 'api', event: 'request_start', meta: { url, method } });
 *   await logger.flush('error');
 */

import { useAuthStore } from '@/lib/auth-store';

export type LogCategory = 'api' | 'router' | 'auth' | 'onboarding' | 'error';

export interface LogEntry {
  ts: number;
  category: LogCategory;
  event: string;
  meta: Record<string, unknown>;
}

const STORAGE_KEY = 'jf-debug-log';
const MAX_ENTRIES = 100;
const FLUSH_ENDPOINT = '/api/debug/log';

// Whitelist estricta de claves permitidas en meta. Anything else gets dropped.
// Mantener sincronizada con la lista documentada en el JSDoc.
const ALLOWED_META_KEYS: ReadonlySet<string> = new Set([
  'url',
  'method',
  'status',
  'duration_ms',
  'error_message',
  'pathname',
  'search',
  'correlation_id',
  'tenant_id',
  'from',
  'to',
]);

// Fallback en memoria cuando sessionStorage no esta disponible (SSR, modo
// privado, iframe sandbox). Sigue siendo un ring buffer del mismo tamano.
let memoryBuffer: LogEntry[] = [];

function isSessionStorageAvailable(): boolean {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    // probe: algunos browsers tiran en modo privado al hacer setItem
    const probeKey = '__jf_log_probe__';
    window.sessionStorage.setItem(probeKey, '1');
    window.sessionStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

function readBuffer(): LogEntry[] {
  if (!isSessionStorageAvailable()) {
    return Array.isArray(memoryBuffer) ? memoryBuffer : [];
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeBuffer(entries: LogEntry[]): void {
  // Cap a MAX_ENTRIES dropping the oldest (FIFO).
  const trimmed = entries.length > MAX_ENTRIES
    ? entries.slice(entries.length - MAX_ENTRIES)
    : entries;

  if (!isSessionStorageAvailable()) {
    memoryBuffer = trimmed;
    return;
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // quota exceeded u otro error: degradamos a memoria sin romper.
    memoryBuffer = trimmed;
  }
}

function sanitizeMeta(meta: Record<string, unknown>): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  if (!meta || typeof meta !== 'object') return clean;
  for (const [key, val] of Object.entries(meta)) {
    if (ALLOWED_META_KEYS.has(key) && val !== undefined) {
      clean[key] = val;
    }
  }
  return clean;
}

function getTenantId(): string | undefined {
  // Lectura runtime del store de auth. El ciclo de imports es seguro porque
  // useAuthStore.getState() solo se ejecuta cuando se llama a log(), no en
  // tiempo de inicializacion del modulo.
  try {
    const tenant = useAuthStore.getState().usuario?.tenant;
    return typeof tenant === 'string' && tenant.length > 0 ? tenant : undefined;
  } catch {
    return undefined;
  }
}

export const logger = {
  log(entry: Omit<LogEntry, 'ts'>): void {
    const tenantId = getTenantId();
    const baseMeta = sanitizeMeta(entry.meta || {});
    // Auto-inyectamos tenant_id si no vino explicito (evita que cada caller lo pase).
    if (tenantId && baseMeta.tenant_id === undefined) {
      baseMeta.tenant_id = tenantId;
    }

    const full: LogEntry = {
      ts: Date.now(),
      category: entry.category,
      event: entry.event,
      meta: baseMeta,
    };

    // Console mirror para debugging local. No imprime objetos crudos sin filtrar.
    try {
      console.info(`[${full.category}] ${full.event}`, full.meta);
    } catch {
      // ignore
    }

    const buf = readBuffer();
    buf.push(full);
    writeBuffer(buf);
  },

  getBuffer(): LogEntry[] {
    return readBuffer();
  },

  async flush(reason: 'error' | 'manual'): Promise<void> {
    const entries = readBuffer();
    if (entries.length === 0) return;

    try {
      const response = await fetch(FLUSH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Body: solo entries sanitizadas + razon. No mandamos token ni cookies.
        body: JSON.stringify({ entries, reason }),
        cache: 'no-store',
      });
      if (response.ok) {
        // limpiamos solo si el server confirmo ingest
        logger.clear();
      }
    } catch {
      // No bloqueamos UX en caso de error de red. El buffer queda intacto
      // para el proximo flush.
    }
  },

  clear(): void {
    if (!isSessionStorageAvailable()) {
      memoryBuffer = [];
      return;
    }
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      memoryBuffer = [];
    }
  },
};

// Helper para correlation_id de requests. Fallback a Math.random si crypto
// no disponible (Edge/older browsers).
export function generarCorrelationId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `cid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
